import * as React from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Plus, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";
import CardChip from "./CardChip";
import NewCardForm from "./NewCardForm";

/**
 * ListColumn — a single kanban column. Container is a droppable so cards
 * can be dropped on empty space; each card is individually sortable.
 * Cards drag from any column to any column.
 */
export default function ListColumn({
  list, cards, labels, blockedSet, canEdit, canManage,
  onOpenCard, companyId, boardId,
}) {
  const [addingCard, setAddingCard] = React.useState(false);
  const [editingName, setEditingName] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState(list.name);

  const { setNodeRef, isOver } = useDroppable({ id: `list-drop:${list.id}` });

  const overLimit = list.wip_limit != null && cards.length > list.wip_limit;

  async function saveName() {
    const val = nameDraft.trim();
    if (!val || val === list.name) { setEditingName(false); return; }
    try {
      await api.patch(`/companies/${companyId}/boards/${boardId}/lists/${list.id}`, { name: val });
      setEditingName(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not rename");
    }
  }

  async function toggleDone() {
    try {
      await api.patch(`/companies/${companyId}/boards/${boardId}/lists/${list.id}`, { is_done: !list.is_done });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update");
    }
  }

  return (
    <div
      className="w-[280px] shrink-0 h-full flex flex-col"
      data-testid={`list-column-${list.id}`}
    >
      <div
        className={`rounded-md border transition-colors ${
          list.is_done ? "border-emerald-200 bg-emerald-50/30" :
          overLimit ? "border-amber-300 bg-amber-50/30" : "border-brand-rule bg-brand-canvas"
        } flex flex-col h-full min-h-0`}
      >
        {/* Header */}
        <div className="px-3 py-2 flex items-center gap-2 border-b border-brand-rule">
          {editingName ? (
            <input
              autoFocus
              className="field-input h-7 py-0 px-1.5 text-[13px] font-semibold"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setEditingName(false); setNameDraft(list.name); } }}
              data-testid={`list-name-input-${list.id}`}
            />
          ) : (
            <button
              type="button"
              onClick={canManage ? () => setEditingName(true) : undefined}
              disabled={!canManage}
              className="flex-1 text-left text-[13px] font-semibold text-brand-ink truncate"
              data-testid={`list-name-${list.id}`}
            >
              {list.name}
            </button>
          )}
          <span className="mono-num text-[10.5px] chip" data-testid={`list-count-${list.id}`}>
            {cards.length}{list.wip_limit != null ? `/${list.wip_limit}` : ""}
          </span>
          {list.is_done && <span className="chip chip-emerald text-[10px]" data-testid={`list-done-${list.id}`}>done</span>}
          {overLimit && <span className="chip chip-amber text-[10px]">over</span>}
        </div>

        {/* Card list */}
        <div
          ref={setNodeRef}
          className={`flex-1 min-h-[60px] p-2 space-y-2 overflow-y-auto ${isOver ? "bg-accent/40" : ""}`}
          data-testid={`list-drop-${list.id}`}
        >
          <SortableContext items={cards.map((c) => `card:${c.id}`)} strategy={verticalListSortingStrategy}>
            {cards.map((c) => (
              <CardChip
                key={c.id}
                card={c}
                labels={labels}
                blocked={blockedSet.has(c.id)}
                onClick={() => onOpenCard(c)}
              />
            ))}
          </SortableContext>
          {cards.length === 0 && !addingCard && (
            <div className="text-[11px] text-brand-faint text-center py-4"
                 data-testid={`list-empty-${list.id}`}>
              No cards.
            </div>
          )}
          {addingCard && (
            <NewCardForm
              companyId={companyId}
              boardId={boardId}
              listId={list.id}
              onClose={() => setAddingCard(false)}
            />
          )}
        </div>

        {/* Footer — add card */}
        {canEdit && !addingCard && (
          <button
            type="button"
            onClick={() => setAddingCard(true)}
            className="w-full text-left px-3 py-2 text-[12px] text-brand-muted hover:bg-brand-hush border-t border-brand-rule"
            data-testid={`list-add-card-${list.id}`}
          >
            <Plus size={11} className="inline mr-1" /> Add a card
          </button>
        )}
        {canManage && (
          <button
            type="button"
            onClick={toggleDone}
            className="w-full text-left px-3 py-1.5 text-[11px] text-brand-muted hover:text-brand-violet"
            data-testid={`list-toggle-done-${list.id}`}
          >
            {list.is_done ? "Unset as Done column" : "Mark as Done column"}
          </button>
        )}
      </div>
    </div>
  );
}
